import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import JourneyHeader from "@/components/JourneyHeader";
import { useLoader } from "../contexts/LoaderContext";
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * LoanAmountConfirmation component
 * This page informs the user when their requested loan amount has been updated by the lender's policy.
 * It is specifically used for the MAS lender flow.
 */
const LoanAmountConfirmation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoading } = useLoader();
    const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

    // Extract data passed from the LoanOffer page after the BRE evaluation
    const { new_eligible_amount, initial_loan_amount } = location.state || {};

    /**
     * Handles the user's decision to either continue with the new amount or reject the offer.
     * @param action 'continue' to proceed, 'reject' to go back to all offers.
     */
    const handleAction = async (action: 'continue' | 'reject') => {
        setLoading(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/api/confirm_updated_loan_amount/`, { action });
            if (res.data.success) {
                toast.success(res.data.message);
                navigate(res.data.next_page_url);
            }
        } catch (err: any) {
            console.error("Error confirming loan amount:", err);
            toast.error(err.response?.data?.error || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
            setIsRejectDialogOpen(false);
        }
    };

    /**
     * Formats numbers into INR currency format.
     */
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    // If for some reason the state is missing, we could fetch it, but here we assume it's passed or handle gracefully.
    const isStateMissing = !new_eligible_amount && !initial_loan_amount;

    return (
        <div className="min-h-screen bg-gradient-to-br from-intellend-white to-gray-100 pb-20 font-inter">
            <JourneyHeader />
            <main className="max-w-2xl mx-auto px-4 py-8 sm:py-16">
                <div className="bg-white/90 backdrop-blur-lg shadow-2xl rounded-[2.5rem] p-6 sm:p-10 border border-white/40">
                    <div className="flex flex-col items-center text-center mb-10">
                        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-6 shadow-inner animate-pulse">
                            <AlertCircle className="w-10 h-10 text-amber-600" />
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3 tracking-tight">
                            Requested Amount Updated
                        </h1>
                        <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
                            Following our evaluation process, the eligible loan amount has been adjusted in accordance with lender policy.
                        </p>
                    </div>

                    <div className="space-y-6 mb-12">
                        <div className="flex flex-col sm:flex-row justify-between items-center p-6 bg-gradient-to-r from-gray-50 to-white rounded-3xl border border-gray-100 shadow-sm gap-4">
                            <div className="text-center sm:text-left">
                                <p className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-1">Requested</p>
                                <p className="text-xl font-semibold text-gray-400 line-through decoration-gray-300">
                                    {formatCurrency(initial_loan_amount || 0)}
                                </p>
                            </div>
                            <div className="hidden sm:block h-10 w-[1px] bg-gray-100"></div>
                            <div className="text-center sm:text-right">
                                <p className="text-xs uppercase tracking-wider text-intellend-green font-bold mb-1">New Eligible Amount</p>
                                <p className="text-3xl sm:text-4xl font-black text-intellend-green drop-shadow-sm">
                                    {formatCurrency(new_eligible_amount || 0)}
                                </p>
                            </div>
                        </div>

                        <div className="bg-intellend-green/5 p-5 rounded-2xl border border-intellend-green/10 flex gap-4 items-start">
                            <CheckCircle2 className="w-6 h-6 text-intellend-green flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-intellend-green/80 leading-relaxed font-medium">
                                You can choose to proceed with this eligible amount or reject it to view other available loan offers from different lenders.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <AlertDialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
                            <AlertDialogTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="order-2 sm:order-1 flex-1 h-14 sm:h-16 rounded-2xl text-base font-bold text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-red-500 hover:border-red-100 transition-all duration-300 active:scale-95"
                                >
                                    Reject Offer
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-3xl border-none shadow-2xl p-8 max-w-[90vw] sm:max-w-md">
                                <AlertDialogHeader className="mb-6">
                                    <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6">
                                        <AlertTriangle className="w-8 h-8 text-red-500" />
                                    </div>
                                    <AlertDialogTitle className="text-2xl font-black text-gray-900 mb-2">
                                        Reject All MAS Offers?
                                    </AlertDialogTitle>
                                    <AlertDialogDescription className="text-gray-500 text-base leading-relaxed">
                                        If you reject this offer, <span className="text-red-600 font-bold italic">all other offers from MAS Financial Services</span> will also be rejected as they are linked to the same lender policy. This action cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row gap-3">
                                    <AlertDialogCancel className="w-full sm:w-auto h-12 rounded-xl border-gray-200 font-bold hover:bg-gray-50">
                                        Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction 
                                        onClick={() => handleAction('reject')}
                                        className="w-full sm:w-auto h-12 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold shadow-lg shadow-red-200"
                                    >
                                        Yes, Reject All
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>

                        <Button
                            onClick={() => handleAction('continue')}
                            className="order-1 sm:order-2 flex-1 h-14 sm:h-16 rounded-2xl text-base font-bold bg-intellend-green hover:bg-intellend-green/90 text-white shadow-xl shadow-intellend-green/20 transition-all duration-300 active:scale-95 transform hover:translate-y-[-2px]"
                        >
                            Accept & Continue
                        </Button>
                    </div>

                    {isStateMissing && (
                        <div className="mt-6 p-3 bg-red-50 text-red-600 text-xs rounded-lg text-center font-medium">
                            Warning: Loan details missing. Please try refreshing if calculations don't appear.
                        </div>
                    )}
                </div>

                <div className="mt-10 text-center space-y-2">
                    <p className="text-xs text-gray-400 font-medium">
                        Secure loan processing powered by Intellend Technology
                    </p>
                    <div className="flex justify-center gap-4 text-[10px] text-gray-400 uppercase tracking-widest font-black">
                        <span>Safe</span>
                        <span>•</span>
                        <span>Transparent</span>
                        <span>•</span>
                        <span>Secure</span>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default LoanAmountConfirmation;
